import React, { FC, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'
import axios from 'axios'
import { UAParser } from 'ua-parser-js'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { api, routes } from 'config'
import { processErrorMsg, constants, redirect, storage } from 'utils'
import { BlankLayout } from 'components'
import queryString from 'query-string'

const Home: FC = () => {
  const { authState, oktaAuth } = useOktaAuth()
  const [isLoggingDone, setIsLoggingDone] = useState(
    Boolean(
      sessionStorage.getItem(
        constants.BROWSER_STORAGE_KEY.LOGGING_OPERATION_DONE,
      ),
    ) || false,
  )
  const navigate = useNavigate()
  const location = useLocation()

  console.log(authState, 'authState')

  useEffect(() => {
    const {
      payload: { preferred_username },
    } = oktaAuth.token.decode(authState?.idToken?.idToken)

    storage.setUser(preferred_username)

    /**
     * Logging user's details in AWS S3 bucket for shared password analysis
     */
    ;(async () => {
      if (authState?.isAuthenticated && !isLoggingDone) {
        try {
          const userAgentParser = new UAParser(navigator.userAgent)
          const fpAgent = await FingerprintJS.load()
          const { visitorId } = await fpAgent.get()
          const deviceFingerprint = visitorId || ''
          const redirectURL =
            sessionStorage.getItem(
              constants.BROWSER_STORAGE_KEY.REDIRECT_URL,
            ) || ''

          const {
            payload: { sub, name, email, memberMlsId, officeMlsId },
          } = oktaAuth.token.decode(authState?.idToken.idToken)

          const data = {
            eventType: 'login',
            id: sub,
            published: new Date().toISOString(),
            memberMlsId: memberMlsId,
            email: email,
            name: name,
            deviceFingerprint: deviceFingerprint,
            userAgent: `${userAgentParser.getOS().name} ${
              userAgentParser.getBrowser().name
            }`,
            rawUserAgent: navigator.userAgent,
            redirectUrl: redirectURL || routes.defaultRedirection,
            officeMlsId: officeMlsId,
          }

          try {
            if (process.env.REACT_APP_LOG_USER_DETAILS === 'true') {
              console.log(authState.idToken)
              axios.post(api.logUserDetails(), data, {
                headers: {
                  Authorization: authState?.accessToken?.accessToken,
                },
              })
            }

            setIsLoggingDone(true)
            sessionStorage.setItem(
              constants.BROWSER_STORAGE_KEY.LOGGING_OPERATION_DONE,
              'true',
            )
            sessionStorage.removeItem(
              constants.BROWSER_STORAGE_KEY.REDIRECT_URL,
            )
          } catch (e: any) {
            processErrorMsg(
              e?.response?.data || e,
              'Home',
              `Unable to process api ${api.logUserDetails()}`,
            )
            throw e
          }
        } catch (e: any) {
          setIsLoggingDone(true)

          processErrorMsg(
            e?.response?.data || e,
            'Home',
            "Logging user's details in AWS S3 bucket for shared password analysis",
          )
        }
      }
    })()
  }, [
    oktaAuth.token,
    authState.accessToken,
    authState.idToken,
    authState.isAuthenticated,
    isLoggingDone,
  ])

  if (authState.isAuthenticated && isLoggingDone) {
    const accessTokenData = oktaAuth.token.decode(
      authState.accessToken.accessToken,
    )

    const hasNoAccess =
      sessionStorage.getItem(constants.BROWSER_STORAGE_KEY.HAS_NO_ACCESS) ||
      'false'
    sessionStorage.removeItem(constants.BROWSER_STORAGE_KEY.HAS_NO_ACCESS)

    const userGroups = accessTokenData.payload.groups

    const isActiveMember =
      userGroups?.includes(constants.OKTA_GROUPS.ACTIVE_MEMBERS) ||
      process.env.REACT_APP_IMPERSONATE_LOGIN === 'true'

    const isInactive =
      userGroups?.includes(constants.OKTA_GROUPS.INACTIVE_MEMBERS) ||
      !isActiveMember // Consider a user inactive if they are either missing the Active Member group, or belong to the Inactive Member group

    const needToAcceptTOU =
      !userGroups?.includes(
        constants.OKTA_GROUPS.ACTIVE_MEMBERS_TOU_ACCEPTED,
      ) && process.env.REACT_APP_IMPERSONATE_LOGIN !== 'true'

    if (isInactive) {
      const {
        payload: { memberMlsId },
      } = oktaAuth.token.decode(authState.idToken.idToken)

      oktaAuth.signOut({}).then(() => {
        localStorage.removeItem('okta-cache-storage')
        localStorage.removeItem('okta-original-uri-storage')
        localStorage.removeItem('okta-shared-transaction-storage')
        window.location.href = `${routes.inactiveMemberRedirection}${memberMlsId}`
      })

      return null
    }

    if (needToAcceptTOU) {
      navigate(routes.subscriptionAgreement)
      return null
    }

    // if /no-access page was accessed for any reason (Okta redirects to it if user didn't have access to an application)
    // redirect to public BCOM site that MarCom controls via Contentful
    // but before doing that, logout out of CustomLogin app or we'll just end up in BCOM dashboard
    //if (hasNoAccess === 'true') {
    //oktaAuth.logout().then(() => {
    // after logging out of CustomLogin app
    // redirect to no-access MarCom page
    //window.location.href = routes.BCOM.noAccess
    //})
    //return null
    //}

    if (hasNoAccess === 'true') {
      oktaAuth.signOut({}).then(() => {
        localStorage.removeItem('okta-cache-storage')
        localStorage.removeItem('okta-original-uri-storage')
        localStorage.removeItem('okta-shared-transaction-storage')
        window.location.href = routes.BCOM.noAccess
      })
      return null
    }

    /*
    Login loop protection:
    - Get loopCount from URL if one exists
    - Increment loopCount each time we get here
    - Append new loopCount to the redirect URL 
    - This allows us to check if we reached the threshold and force user logout from CustomLogin
    */
    const loopCountThreshold = 2

    // get loop count from qs param, increment and store again
    const values = queryString.parse(decodeURIComponent(location.search))
    const loopCount: number = +(values.loopCount || '0')

    // determine how to handle loopCount QS param depending whether or not there's already QS param present
    let loopCountPrefix = '?'
    const fromURI = values.fromURI
    if (typeof fromURI === 'string') {
      if (fromURI.includes('?')) {
        loopCountPrefix = '&'
      }
    }

    if (loopCount === 0) {
      location.search += `${loopCountPrefix}loopCount=${loopCount + 1}`
    } else {
      location.search = location.search.replace(
        encodeURIComponent(`loopCount=${loopCount}`),
        `loopCount=${loopCount + 1}`,
      )
    }

    // test to see if we are in a loop
    if (loopCount >= loopCountThreshold) {
      // if in loop, redirect to logout
      processErrorMsg(
        `Loop detected. Redirecting to logout. Location being accessed: ${JSON.stringify(
          location,
        )}`,
        'Home',
        'Login loop protection',
      )

      // redirect to logout so user can log in again
      window.location.href = routes.logout
    } else {
      // if not in the loop, redirect to whatever 'location' is
      console.log('redirecting location.search=' + location.search)
      console.log('redirecting location=' + location)
      redirect(location)
    }
  }

  return <BlankLayout />
}

export default Home
